import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Close, Add } from "@mui/icons-material";
import editIcon from "../../assets/editIcon.png";
import CustomInputField from "../common/CustomInputField";
import { predictionService } from "../../services/predictionService";
import { getPageNumbers } from "../../utils/pagination";
// prediction types come from API; no static category options needed here
// end of imports
const Prediction = () => {
  const [predictions, setPredictions] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);

  const [predictionName, setPredictionName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [neutralMessage, setNeutralMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // add | edit
  const [editingPrediction, setEditingPrediction] = useState(null);
  const [originalPrediction, setOriginalPrediction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  // Count words in the prompt
  // word count not needed currently

  // Load predictions
  const loadPredictions = async () => {
    setFetchLoading(true);
    try {
      const result = await predictionService.getPredictions();
      if (result.success) {
        // normalize data
        const items = (result.data.result || result.data).map((item) => ({
          id: item._id?.$oid || item.id,
          name: item.name,
          type: item.type,
          prompt: item.prompt,
          createdAt: item.created_at?.$date || item.created_at || null,
          updatedAt: item.updated_at?.$date || item.updated_at || null,
        }));
        setPredictions(items);
        setTotalPages(Math.ceil(items.length / itemsPerPage));
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("Failed to fetch predictions. Please try again.");
    } finally {
      setFetchLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Get current page items for pagination
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return predictions.slice(startIndex, endIndex);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadPredictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePromptChange = (event) => {
    setPrompt(event.target.value);
  };

  const handleSet = async () => {
    setError("");
    setSuccess("");
    setFieldErrors({});

    // If editing and no changes, show neutral message
    if (modalMode === 'edit' && originalPrediction) {
      const current = { name: predictionName || '', prompt: prompt || '' };
      const orig = { name: originalPrediction.name || '', prompt: originalPrediction.prompt || '' };
      if (JSON.stringify(current) === JSON.stringify(orig)) {
        setNeutralMessage('No changes to save.');
        setTimeout(() => setNeutralMessage(''), 3000);
        return;
      }
    }

    // Validation
    const errors = {};

    if (!predictionName.trim()) {
      errors.predictionName = "Prediction name is required";
    }

    if (!prompt.trim()) {
      errors.prompt = "Prompt is required";
    } else if (prompt.trim().length < 10) {
      errors.prompt = "Prompt must be at least 10 characters long";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      let result;
      if (modalMode === "edit" && editingPrediction) {
        result = await predictionService.updatePrediction(editingPrediction, {
          name: predictionName.trim(),
          prompt: prompt.trim(),
        });
      } else {
        result = await predictionService.createPrediction({
          name: predictionName.trim(),
          prompt: prompt.trim(),
        });
      }

      if (result.success) {
        setSuccess(result.message);
        // Reset form
        setPredictionName("");
        setPrompt("");
        // Refresh list
        await loadPredictions();
        setModalOpen(false);
        setModalMode("add");
        setEditingPrediction(null);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setModalMode("add");
    setEditingPrediction(null);
    setPredictionName("");
    setPrompt("");
    setError("");
    setSuccess("");
    setFieldErrors({});
    setNeutralMessage("");
    setOriginalPrediction(null);
    setModalOpen(true);
  };

  const handleEditPrediction = (id) => {
    const p = predictions.find((x) => x.id === id);
    if (!p) return;
    setPredictionName(p.name);
    setPrompt(p.prompt);
    setModalMode("edit");
    setEditingPrediction(id);
    setNeutralMessage("");
    setOriginalPrediction({ name: p.name, prompt: p.prompt });
    setModalOpen(true);
  };

  // const handleDeletePrediction = (id) => {
  //   // open custom delete confirmation dialog instead of window.confirm
  //   setDeleteTargetId(id);
  //   setDeleteDialogOpen(true);
  // };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    setError("");
    try {
      const res = await predictionService.deletePrediction(deleteTargetId);
      if (res.success) {
        await loadPredictions();
          setDeleteDialogOpen(false);
          setDeleteTargetId(null);
          setSuccess("Prediction deleted successfully.");
          setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError("Failed to delete. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalMode("add");
    setEditingPrediction(null);
    setError("");
    setSuccess("");
    setFieldErrors({});
  };

  const handleResetPrediction = () => {
    if (modalMode === 'edit' && originalPrediction) {
      setPredictionName(originalPrediction.name || '');
      setPrompt(originalPrediction.prompt || '');
    } else {
      setPredictionName('');
      setPrompt('');
    }
    setFieldErrors({});
  };

  const isPromptChanged = () => {
    const current = (prompt || "").trim();
    const original = (originalPrediction?.prompt || "").trim();
    if (modalMode === 'edit') {
      return current !== original;
    }
    return current !== "";
  };

  return (
    <Box
      sx={{
        padding: "24px",
        backgroundColor: "#000000",
        minHeight: "100vh",
        paddingTop: 0
      }}
    >
      {/* Alerts - Full Width */}
      <Box sx={{ position: "relative", left: "-24px", width: "calc(100% + 48px)", marginBottom: 3 }}>
        {success && (
          <Alert 
            severity="success"
            icon={false}
            onClose={() => setSuccess("")} 
            sx={{ 
              backgroundColor: "#4caf50", 
              color: "#FFFFFF",
              borderRadius: 0,
              padding: "16px 24px",
              fontSize: "18px",
              fontWeight: 500,
              border: "none",
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              "& .MuiAlert-message": {
                color: "#FFFFFF",
                width: "100%",
                fontSize: "18px",
                display: "flex",
                alignItems: "center"
              },
              "& .MuiAlert-action": {
                color: "#FFFFFF",
                paddingTop: 0
              }
            }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error"
            icon={false}
            onClose={() => setError("")} 
            sx={{ 
              backgroundColor: "#f44336", 
              color: "#FFFFFF",
              borderRadius: 0,
              padding: "16px 24px",
              fontSize: "18px",
              fontWeight: 500,
              border: "none",
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              "& .MuiAlert-message": {
                color: "#FFFFFF",
                width: "100%",
                fontSize: "18px",
                display: "flex",
                alignItems: "center"
              },
              "& .MuiAlert-action": {
                color: "#FFFFFF",
                paddingTop: 0
              }
            }}
          >
            {error}
          </Alert>
        )}
        {neutralMessage && (
          <Alert 
            severity="info"
            icon={false}
            onClose={() => setNeutralMessage("")} 
            sx={{ 
              backgroundColor: "#2196f3", 
              color: "#FFFFFF",
              borderRadius: 0,
              padding: "16px 24px",
              fontSize: "18px",
              fontWeight: 500,
              border: "none",
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              "& .MuiAlert-message": {
                color: "#FFFFFF",
                width: "100%",
                fontSize: "18px",
                display: "flex",
                alignItems: "center"
              },
              "& .MuiAlert-action": {
                color: "#FFFFFF",
                paddingTop: 0
              }
            }}
          >
            {neutralMessage}
          </Alert>
        )}
      </Box>

      {/* Header Section */}
      <Box
        sx={{
          width: "100%",
          marginBottom: "40px",
        }}
      >
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: "32px",
            fontWeight: 600,
            marginBottom: "8px",
          }}
        >
          Manage Prediction
        </Typography>
        <Typography
          sx={{
            color: "#888888",
            fontSize: "16px",
          }}
        >
          Create, edit, and manage prediction content.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <Typography
          sx={{ color: "#FFFFFF", fontSize: "24px", fontWeight: 600 }}
        >
          Customize Prediction
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenModal}
          sx={{
            backgroundColor: "#E1AD01",
            color: "#000000",
            borderRadius: "25px",
            padding: "12px 24px",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { backgroundColor: "#B8941F" },
          }}
        >
          Add Prediction
        </Button>
      </Box>

      <Box>
        {fetchLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
            <CircularProgress sx={{ color: "#E1AD01" }} />
          </Box>
        ) : predictions.length === 0 ? (
          <Box sx={{ textAlign: "center", padding: 4 }}>
            <Typography sx={{ color: "#CCCCCC", fontSize: "18px" }}>
              No predictions found
            </Typography>
          </Box>
        ) : (
          getCurrentPageItems().map((p) => (
            <Card
              key={p.id}
              sx={{
                backgroundColor: "#2A2A2A",
                borderRadius: "16px",
                marginBottom: 3,
                border: "1px solid #333333",
              }}
            >
              <CardContent sx={{ padding: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 2,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box>
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontSize: "20px",
                          fontWeight: 600,
                        }}
                      >
                        {p.name}
                      </Typography>
                      {p.createdAt && (
                        <Typography
                          sx={{
                            color: "#888888",
                            fontSize: "12px",
                            marginTop: 0.5,
                            marginBottom: 1,
                          }}
                        >
                          Created: {new Date(p.createdAt).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {p.updatedAt && (
                            <>
                              {' | Updated: '}
                              {new Date(p.updatedAt).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </>
                          )}
                        </Typography>
                      )}
                      <Typography sx={{
                        color: "#CCCCCC",
                        fontSize: "14px",
                        lineHeight: 1.6,
                        display: '-webkit-box',
                        WebkitLineClamp: expandedPrompts[p.id] ? 'unset' : 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-all'
                      }}>
                        {p.prompt}
                      </Typography>
                      {p.prompt?.length > 150 && (
                        <Typography
                          component="span"
                          onClick={() => setExpandedPrompts(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                          sx={{
                            color: "#E1AD01",
                            cursor: "pointer",
                            marginTop: 0.5,
                            display: 'inline-block',
                            fontWeight: 600,
                            fontSize: "14px",
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline', color: '#B8941F' }
                          }}
                        >
                          {expandedPrompts[p.id] ? 'View Less' : 'View More'}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      onClick={() => handleEditPrediction(p.id)}
                      sx={{
                        backgroundColor: "#E1AD01",
                        color: "#000",
                        width: 40,
                        height: 40,
                        "&:hover": { backgroundColor: "#B8941F" },
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img src={editIcon} alt="Edit" style={{ width: 20, height: 20 }} />
                    </IconButton>
                    {/* <IconButton
                      onClick={() => handleDeletePrediction(p.id)}
                      sx={{
                        backgroundColor: "#E53935",
                        color: "#fff",
                        width: 40,
                        height: 40,
                        "&:hover": { backgroundColor: "#C62828" },
                      }}
                    >
                      <Delete sx={{ fontSize: 20 }} />
                    </IconButton> */}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* Pagination */}
      {predictions.length > itemsPerPage && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            marginTop: 4,
            marginBottom: 4,
          }}
        >
          <Button
            variant="outlined"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            sx={{
              borderColor: "#666666",
              color: "#CCCCCC",
              borderRadius: "20px",
              padding: "8px 20px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#E1AD01",
                backgroundColor: "rgba(212, 175, 55, 0.1)",
              },
              "&:disabled": {
                borderColor: "#333333",
                color: "#666666",
              },
            }}
          >
            Previous
          </Button>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {getPageNumbers(currentPage, totalPages).map((page, idx) =>
              page === "..." ? (
                <Box key={`ellipsis-${idx}`} sx={{ color: "#999999", fontSize: "14px", px: 0.5 }}>...</Box>
              ) : (
                <Box
                  key={page}
                  onClick={() => handlePageChange(page)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor:
                      page === currentPage ? "#E1AD01" : "#333333",
                    color: page === currentPage ? "#000000" : "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor:
                        page === currentPage ? "#B8941F" : "#444444",
                    },
                  }}
                >
                  {page}
                </Box>
              )
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            sx={{
              backgroundColor: "#E1AD01",
              color: "#000000",
              borderRadius: "20px",
              padding: "8px 20px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#B8941F",
              },
              "&:disabled": {
                backgroundColor: "#333333",
                color: "#666666",
              },
            }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Delete confirmation dialog (styled similar to logout modal) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1A1A1A",
            borderRadius: "20px",
            border: "1px solid #333333",
            padding: "8px",
            minWidth: "400px"
          }
        }}
      >
        <DialogContent sx={{ padding: "40px 32px 32px 32px", textAlign: "center" }}>
          <Typography sx={{
            fontSize: "28px",
            fontWeight: 600,
            color: "#FFFFFF",
            marginBottom: 3
          }}>Delete</Typography>

          <Typography sx={{
            fontSize: "18px",
            color: "#CCCCCC",
            marginBottom: 5,
            lineHeight: 1.4
          }}>
            Are you sure you want to delete this prediction?
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", alignItems: "center" }}>
            <Button
              variant="outlined"
              onClick={handleCancelDelete}
              sx={{
                borderColor: "#555555",
                color: "#CCCCCC",
                backgroundColor: "transparent",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "16px",
                minWidth: "120px",
                "&:hover": {
                  borderColor: "#777777",
                  backgroundColor: "rgba(255, 255, 255, 0.05)"
                }
                ,
                "&.Mui-disabled": {
                  borderColor: "#777777",
                  color: "#999999",
                  cursor: "not-allowed",
                  pointerEvents: "auto"
                }
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleConfirmDelete}
              disabled={deleteLoading}
              sx={{
                backgroundColor: "#E1AD01",
                color: "#000000",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "16px",
                minWidth: "120px",
                "&:hover": { backgroundColor: "#B8941F" },
                "&.Mui-disabled": {
                  cursor: "not-allowed",
                  backgroundColor: "#b8941fb3",
                  pointerEvents: "auto",
                  color: "#666666"
                }
              }}
            >
              {deleteLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#000" }} />
                  Delete
                </Box>
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: "20px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px 32px 16px 32px",
          }}
        >
          <Typography
            sx={{ color: "#333333", fontSize: "24px", fontWeight: 600 }}
          >
            {modalMode === "edit" ? "Edit Prediction" : "Add Prediction"}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            sx={{ color: "#666666", "&:hover": { backgroundColor: "#F0F0F0" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 32px 32px 32px" }}>
          {error && (
            <Alert
              severity="error"
              sx={{
                marginBottom: 3,
                backgroundColor: "#ffebee",
                color: "#c62828",
                whiteSpace: "pre-line"
              }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{
                marginBottom: 3,
                backgroundColor: "#e8f5e8",
                color: "#2e7d32",
              }}
            >
              {success}
            </Alert>
          )}
          {neutralMessage && (
            <Alert
              severity="info"
              sx={{ marginBottom: 3, backgroundColor: "#f1f8ff", color: "#155fa0" }}
            >
              {neutralMessage}
            </Alert>
          )}

          <Box sx={{ marginBottom: 3, marginTop: 2 }}>
            <CustomInputField
              label="Prediction Name"
              placeholder="Enter"
              value={predictionName}
              onChange={(e) => {
                setPredictionName(e.target.value);
                if (fieldErrors.predictionName) {
                  setFieldErrors(prev => ({ ...prev, predictionName: "" }));
                }
              }}
              fullWidth
            />
            {fieldErrors.predictionName && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.predictionName}
              </Typography>
            )}
          </Box>

          <Box sx={{ marginBottom: 3 }}>
            <CustomInputField
              label="Prompt"
              placeholder="Type Here"
              value={prompt}
              onChange={(e) => {
                handlePromptChange(e);
                if (fieldErrors.prompt) {
                  setFieldErrors(prev => ({ ...prev, prompt: "" }));
                }
              }}
              multiline
              rows={6}
              
            />
            {fieldErrors.prompt && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.prompt}
              </Typography>
            )}
            
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleResetPrediction}
              disabled={!isPromptChanged()}
              sx={{
                borderColor: "#E1AD01",
                color: "#E1AD01",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleSet}
              disabled={loading || (modalMode === 'edit' && originalPrediction && JSON.stringify({ name: predictionName || '', prompt: prompt || '' }) === JSON.stringify({ name: originalPrediction.name || '', prompt: originalPrediction.prompt || '' }))}
              sx={{
                backgroundColor: "#E1AD01",
                color: "#000000",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                "&.Mui-disabled": {
                  cursor: "not-allowed",
                  backgroundColor: "#b8941fb3",
                  pointerEvents: "auto",
                  color: "#666666"
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#000" }} />
                  {modalMode === "edit" ? "Updating..." : "Adding..."}
                </Box>
              ) : modalMode === "edit" ? (
                "Update Prediction"
              ) : (
                "Add Prediction"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Prediction;
